import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { GeodataMarker } from '../../../../../../typings/api/skymap/rest/v0/geodata';
import { SkyMapAxiosServiceFactory } from '../../../../services/axios/skymap-axios-service-factory';
import { useToaster } from '../../../state/toaster-state';

/**
 * Deletes a marker optimistically: removes it from the getMarkers cache immediately,
 * then reverts on failure.
 */
export function useDeleteMarker(geodataId: string) {
  const queryClient = useQueryClient();
  const { showToast } = useToaster();
  const { t } = useTranslation('cloudProcessing');

  const queryKey = useMemo(() => ['getGeodataMarkers', geodataId] as const, [geodataId]);

  const deleteMarker = useCallback(
    async (markerId: string) => {
      const previousData = queryClient.getQueryData<GeodataMarker[]>(queryKey);
      const previousMarker = previousData?.find((m) => m.id === markerId);

      // Optimistic removal
      queryClient.setQueryData<GeodataMarker[] | undefined>(queryKey, (prev) =>
        prev?.filter((m) => m.id !== markerId),
      );

      try {
        await SkyMapAxiosServiceFactory.instance.createGeodataServiceV0().deleteMarker({
          path: { geodataId, markerId },
        });
      } catch {
        // Revert optimistic removal — guard against duplicates if a refetch
        // already restored the marker while the API call was in-flight.
        if (previousMarker) {
          queryClient.setQueryData<GeodataMarker[] | undefined>(queryKey, (prev) => {
            if (!prev) {
              return [previousMarker];
            }
            if (prev.some((m) => m.id === previousMarker.id)) {
              return prev;
            }
            return [...prev, previousMarker];
          });
        }
        showToast({
          type: 'error',
          title: t('toaster.title.error', { ns: 'common' }),
          message: t('reviewGcpMarkerEstimatesDialog.someMarkersFailedToUpdate', {
            ns: 'cloudProcessing',
          }),
        });
      }
    },
    [geodataId, queryKey, queryClient, showToast, t],
  );

  return { deleteMarker };
}
